// admin-analytics-expansion P2b (tasks 2b.2) — TicketBrowseTable
//
// Renders the cursor-paginated ticket browse list. Mirrors AuditLogTable's
// loading/empty/"Load more" conventions. Rows are click-navigable to the
// ticket detail route `/dashboard/tickets/$ticketId` (P3b, registered in
// `_authenticated.dashboard.tickets.$ticketId.index.tsx`).

import { useTranslation } from '@jq4race/i18n';
import { paymentDisplayState, type TicketBrowseItem } from '@jq4race/shared';
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { useNavigate } from '@tanstack/react-router';

import { formatTimestamp } from '../../../../shared/format/format-timestamp';

const STATUS_TONE: Record<TicketBrowseItem['status'], BadgeProps['variant']> = {
  parked: 'info',
  requested: 'warning',
  in_transit: 'accent',
  delivered: 'success',
  cancelled: 'danger',
};

const STATUS_LABEL_KEY: Record<TicketBrowseItem['status'], string> = {
  parked: 'status.parked',
  requested: 'status.requested',
  in_transit: 'status.inTransit',
  delivered: 'status.delivered',
  cancelled: 'status.cancelled',
};

// QA R3-10: an unpaid ticket whose total is $0 owes nothing -> neutral "No charge".
const PAYMENT_STATE_TONE: Record<'paid' | 'due' | 'no_charge', BadgeProps['variant']> = {
  paid: 'success',
  due: 'warning',
  no_charge: 'secondary',
};

const PAYMENT_STATE_LABEL_KEY: Record<'paid' | 'due' | 'no_charge', string> = {
  paid: 'paymentStatus.paid',
  due: 'paymentStatus.unpaid',
  no_charge: 'paymentStatus.noCharge',
};

function PaymentBadge({ item, t }: { item: TicketBrowseItem; t: (key: string) => string }) {
  const state = paymentDisplayState(item) ?? 'due';
  return <Badge variant={PAYMENT_STATE_TONE[state]}>{t(PAYMENT_STATE_LABEL_KEY[state])}</Badge>;
}

interface TicketBrowseTableProps {
  items: TicketBrowseItem[];
  isLoading: boolean;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  locationNameById: Map<string, string>;
  /**
   * locationId -> IANA timezone, so each row's check-in time is shown on its
   * own location's clock. Unknown locations fall back to the browser timezone.
   */
  locationTimeZoneById?: Map<string, string>;
}

export function TicketBrowseTable({
  items,
  isLoading,
  hasMore,
  isFetchingNextPage,
  onLoadMore,
  locationNameById,
  locationTimeZoneById,
}: TicketBrowseTableProps) {
  const { t, i18n } = useTranslation('tickets');
  const navigate = useNavigate();

  function goToDetail(ticketId: string) {
    navigate({ to: '/dashboard/tickets/$ticketId', params: { ticketId } });
  }

  if (isLoading) {
    return (
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.ticketNumber')}</TableHead>
                <TableHead>{t('table.plate')}</TableHead>
                <TableHead>{t('table.status')}</TableHead>
                <TableHead>{t('table.payment')}</TableHead>
                <TableHead>{t('table.location')}</TableHead>
                <TableHead>{t('table.checkedIn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton rows
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: loading skeleton cells
                    <TableCell key={`cell-${j}`}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.ticketNumber')}</TableHead>
                <TableHead>{t('table.plate')}</TableHead>
                <TableHead>{t('table.status')}</TableHead>
                <TableHead>{t('table.payment')}</TableHead>
                <TableHead>{t('table.location')}</TableHead>
                <TableHead>{t('table.checkedIn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableEmpty colSpan={6}>
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                    <p className="text-[14px] font-medium text-jq-charcoal">{t('empty.browse')}</p>
                  </div>
                </TableEmpty>
              ) : (
                items.map((item) => (
                  <TableRow
                    key={item.id}
                    onClick={() => goToDetail(item.id)}
                    className="cursor-pointer hover:bg-jq-surface-1/50"
                  >
                    <TableCell className="font-mono text-[13px] text-jq-navy">
                      {item.ticketNumber}
                    </TableCell>
                    <TableCell className="font-mono text-[13px] text-jq-charcoal">
                      {item.plate}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_TONE[item.status]}>
                        {t(STATUS_LABEL_KEY[item.status])}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <PaymentBadge item={item} t={t} />
                    </TableCell>
                    <TableCell className="text-[13px] text-jq-graphite">
                      {locationNameById.get(item.locationId) ?? '—'}
                    </TableCell>
                    <TableCell className="font-mono text-[12px] text-jq-graphite">
                      {formatTimestamp(
                        item.checkedInAt,
                        i18n.language,
                        locationTimeZoneById?.get(item.locationId),
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="text-[13px]"
          >
            {isFetchingNextPage ? t('common:states.loading') : t('loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
